/**
 * RecentJobs Component
 * Premium job list with pagination and Lucide icons
 */

import React, { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, Button } from "../common";
import { JobTable } from "../jobs/JobTable";
import { useAppDispatch, useAppSelector, jobsActions } from "../../redux";
import {
  RefreshCw,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronFirst,
  ChevronLast,
} from "lucide-react";

interface RecentJobsProps {
  onRefresh?: () => void;
}

export const RecentJobs: React.FC<RecentJobsProps> = ({ onRefresh }) => {
  const dispatch = useAppDispatch();
  const { jobs, loading, pagination } = useAppSelector((state: any) => state.jobs);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const fetchJobs = useCallback(
    (page: number) => {
      dispatch(jobsActions.fetchJobs({ page, page_size: pageSize }));
    },
    [dispatch, pageSize]
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      fetchJobs(currentPage);
      onRefresh?.();
    } finally {
      setRefreshing(false);
    }
  }, [fetchJobs, currentPage, onRefresh]);

  useEffect(() => {
    fetchJobs(1);
  }, [fetchJobs]);

  const handleDelete = async (jobId: string) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      try {
        await dispatch(jobsActions.deleteJob(jobId)).unwrap();
        handleRefresh();
      } catch (error) {
        console.error("Failed to delete job:", error);
      }
    }
  };

  // Auto-refresh for processing jobs
  useEffect(() => {
    const interval = setInterval(() => {
      const hasActiveJobs = jobs.some(
        (job: any) => job.status === "processing" || job.status === "pending"
      );
      if (hasActiveJobs) {
        fetchJobs(currentPage);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [jobs, currentPage, fetchJobs]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchJobs(page);
  };

  // Pagination calculations using correct Redux field names
  const totalItems = pagination?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  return (
    <Card variant="glass" padding="lg">
      <CardHeader
        title="All Jobs"
        subtitle="Track and manage your analysis jobs"
        icon={<Clock className="w-5 h-5" />}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            icon={<RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />}
          >
            Refresh
          </Button>
        }
      />

      <JobTable
        jobs={jobs}
        loading={loading}
        onDelete={handleDelete}
        onRefresh={handleRefresh}
      />

      {/* Pagination Controls */}
      {jobs.length > 0 && totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-700/50 pt-4">
          {/* Page Info */}
          <div className="text-slate-400 text-sm">
            Showing {(currentPage - 1) * pageSize + 1} -{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} jobs
          </div>

          {/* Page Navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1 || loading}
              icon={<ChevronFirst className="w-4 h-4" />}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              icon={<ChevronLeft className="w-4 h-4" />}
            />

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                      currentPage === pageNum
                        ? "bg-gradient-to-r from-primary-500 to-accent-500 text-white"
                        : "bg-slate-800/50 text-slate-300 hover:bg-slate-700 border border-slate-700/50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              icon={<ChevronRight className="w-4 h-4" />}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage >= totalPages || loading}
              icon={<ChevronLast className="w-4 h-4" />}
            />
          </div>
        </div>
      )}
    </Card>
  );
};
